import { useState, useRef, useCallback, KeyboardEvent } from 'react';

interface CharacterPaletteProps {
  onCharacterSelect: (char: string) => void;
  showCopyFeedback?: boolean;
  /** Tighter layout for use inside another card (e.g. reaction arrow panel) */
  embedded?: boolean;
}

const CHARACTERS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '⁺', '⁻', 'Δ', '·', '≡', '°'];

// Descriptive names for screen readers
const CHARACTER_DESCRIPTIONS: Record<string, string> = {
  '⁰': 'superscript zero',
  '¹': 'superscript one',
  '²': 'superscript two',
  '³': 'superscript three',
  '⁴': 'superscript four',
  '⁵': 'superscript five',
  '⁶': 'superscript six',
  '⁷': 'superscript seven',
  '⁸': 'superscript eight',
  '⁹': 'superscript nine',
  '⁺': 'superscript plus',
  '⁻': 'superscript minus',
  'Δ': 'delta',
  '·': 'middle dot',
  '≡': 'triple bond',
  '°': 'degree',
};

export function CharacterPalette({
  onCharacterSelect,
  showCopyFeedback = false,
  embedded = false,
}: CharacterPaletteProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleClick = (char: string, index: number) => {
    onCharacterSelect(char);

    if (showCopyFeedback) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 400);
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let targetIndex = index;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        targetIndex = Math.min(index + 1, CHARACTERS.length - 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        targetIndex = Math.max(index - 1, 0);
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = CHARACTERS.length - 1;
        break;
      default:
        return;
    }

    buttonRefs.current[targetIndex]?.focus();
  }, []);

  return (
    <div
      className={
        embedded
          ? 'flex w-full flex-wrap gap-1 justify-start'
          : 'mb-4 flex flex-wrap gap-1 justify-center'
      }
      role="toolbar"
      aria-label="Special character palette"
    >
      {CHARACTERS.map((char, index) => (
        <button
          key={char}
          ref={(el) => (buttonRefs.current[index] = el)}
          onClick={() => handleClick(char, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className={`character-btn ${copiedIndex === index ? 'copied' : ''}`}
          aria-label={`Insert ${CHARACTER_DESCRIPTIONS[char] || char}`}
          tabIndex={index === 0 ? 0 : -1}
        >
          {char}
        </button>
      ))}
    </div>
  );
}
